<template>
  <div class="relative flex items-center space-x-2">
    <!-- Dynamic ping indicator based on current status -->
    <div class="relative">
      <div :class="[
        'w-2 h-2 rounded-full',
        getStatusColorClass(currentStatus.status)
      ]"></div>
      <div 
        v-if="shouldShowPing"
        :class="[
          'absolute top-0 left-0 w-2 h-2 rounded-full animate-ping',
          getStatusColorClass(currentStatus.status, true)
        ]"
      ></div>
    </div>
    
    <!-- Dynamic status text with proper color -->
    <div 
      :class="[
        'text-xs font-medium cursor-pointer relative',
        getTextColorClass(currentStatus.status)
      ]"
      @mouseenter="showTooltip = true"
      @mouseleave="showTooltip = false"
    >
      {{ currentStatus.statusText }}
    </div>
    
    <!-- Tooltip dropdown - positioned outside the container with portal-like behavior -->
    <Teleport to="body">
      <transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-2 scale-95"
      >
        <div 
          v-if="showTooltip"
          ref="tooltip"
          class="fixed z-[9999] pointer-events-none"
          :style="tooltipStyle"
        >
          <div class="bg-neutral-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
            {{ currentStatus.message }}
            <!-- Tooltip arrow -->
            <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { getUserStatus, getCachedUserStatus, getStatusColorClass, getTextColorClass, startStatusMonitoring } from '../lib/chatAppwrite.js'

export default {
  name: 'StatusDropdown',
  props: {
    companyId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showTooltip: false,
      currentStatus: {
        status: 'online',
        statusText: 'Online',
        message: 'Wir sind gerade online und bereit, dir zu helfen!'
      },
      statusMonitorCleanup: null,
      effectiveCompanyId: null
    }
  },
  async mounted() {
    console.log('🚀 StatusDropdown mounted with prop companyId:', this.companyId);
    await this.determineCompanyId();
    await this.loadUserStatus();
    this.startMonitoring();
  },
  beforeUnmount() {
    this.stopMonitoring();
  },
  computed: {
    tooltipStyle() {
      if (!this.showTooltip) return {};
      
      const onlineElement = this.$el?.querySelector('.cursor-pointer');
      if (!onlineElement) return {};
      
      const rect = onlineElement.getBoundingClientRect();
      return {
        top: `${rect.top - 45}px`,
        left: `${rect.left + rect.width / 2}px`,
        transform: 'translateX(-50%)'
      };
    },
    shouldShowPing() {
      // Only show ping animation for online and busy status
      return ['online', 'busy'].includes(this.currentStatus.status);
    }
  },
  watch: {
    companyId: {
      handler(newCompanyId, oldCompanyId) {
        console.log('🔄 CompanyId prop changed from', oldCompanyId, 'to', newCompanyId);
        this.determineCompanyId().then(() => {
          this.loadUserStatus();
        });
      },
      immediate: true
    }
  },
  methods: {
    async determineCompanyId() {
      // Try multiple sources for company ID
      let companyId = this.companyId;
      
      // If no prop company ID, try to get from chat state or customer data
      if (!companyId) {
        console.log('🔍 No companyId prop, checking chat state...');
        
        // Try to get from window/global chat state if available
        if (window.chatState?.customer?.companyId) {
          companyId = window.chatState.customer.companyId;
          console.log('✅ Found companyId in chat state:', companyId);
        } else if (window.chatState?.conversation?.companyId) {
          companyId = window.chatState.conversation.companyId;
          console.log('✅ Found companyId in conversation:', companyId);
        } else {
          // As last resort, use hardcoded test company ID (you mentioned this in logs)
          companyId = '681b16480017f5f55c58';
          console.log('⚠️ Using fallback companyId:', companyId);
        }
      }
      
      this.effectiveCompanyId = companyId;
      console.log('💼 Effective company ID determined:', this.effectiveCompanyId);
    },
    
    async loadUserStatus() {
      try {
        console.log('📡 Loading user status for company:', this.effectiveCompanyId);
        
        if (!this.effectiveCompanyId) {
          console.warn('⚠️ No effective company ID available');
          return;
        }
        
        // Always fetch fresh status for debugging
        const status = await getUserStatus(this.effectiveCompanyId);
        this.currentStatus = status;
        
        console.log('✅ Status loaded and set:', {
          companyId: this.effectiveCompanyId,
          status: status.status,
          statusText: status.statusText,
          message: status.message
        });
        
        // Force reactivity update
        this.$forceUpdate();
        
      } catch (error) {
        console.error('❌ Error loading user status:', error);
        // Keep default online status on error
        this.currentStatus = {
          status: 'online',
          statusText: 'Online',
          message: 'Wir sind gerade online und bereit, dir zu helfen!'
        };
      }
    },
    
    startMonitoring() {
      if (!this.effectiveCompanyId) {
        console.warn('⚠️ Cannot start monitoring without effective companyId');
        return;
      }
      
      console.log('🔄 Starting status monitoring for company:', this.effectiveCompanyId);
      // Start monitoring status changes
      this.statusMonitorCleanup = startStatusMonitoring(
        this.effectiveCompanyId,
        this.onStatusChange
      );
    },
    
    stopMonitoring() {
      if (this.statusMonitorCleanup) {
        console.log('🛑 Stopping status monitoring');
        this.statusMonitorCleanup();
        this.statusMonitorCleanup = null;
      }
    },
    
    onStatusChange(newStatus, previousStatus) {
      console.log(`📢 Status changed from ${previousStatus} to ${newStatus.status}`);
      this.currentStatus = newStatus;
      this.$forceUpdate(); // Force reactivity
    },
    
    getStatusColorClass(status, isPing = false) {
      return getStatusColorClass(status, isPing);
    },
    
    getTextColorClass(status) {
      return getTextColorClass(status);
    }
  }
}
</script>